// src/components/festival/UzairueKingdomPortal.tsx
// Uzairue Kingdom Portal — Full Kingdom portal under Kingdom Menu
// Sections: Overview | Officials | Royal Messages | Announcements | Cultural Events | Gallery | History
import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Plus, Trash2, ChevronDown, ChevronUp, Save, Upload } from 'lucide-react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444', amber:'#f59e0b', green:'#22c55e',
};
const inp: React.CSSProperties = {
  width:'100%', background:C.bg, border:`1px solid ${C.border}`,
  borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13,
  outline:'none', boxSizing:'border-box',
};
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  background:bg, color, border:'none', borderRadius:7, padding:'8px 16px',
  fontSize:13, fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
});

function authHdr() {
  const t = localStorage.getItem('erue_auth_token') || localStorage.getItem('auth_token') || '';
  return { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) };
}

const DEPT = ['Secretariat','Protocol','Finance','Information','Cultural Affairs','Security','Community Relations'];
const ROLES = ['His Royal Majesty','Palace Secretary','Chief of Staff','Protocol Officer',
               'Royal Adviser','Cultural Attache','Community Liaison','Finance Officer'];

interface Props { currentUser?: any; }

export default function UzairueKingdomPortal({ currentUser }: Props) {
  const isAdmin = !!(
    currentUser?.systemRole === 'PLATFORM_ADMIN' ||
    currentUser?.role === 'admin' ||
    currentUser?.email === 'erueuzairue@gmail.com'
  );

  type TabId = 'overview'|'officials'|'messages'|'announcements'|'events'|'gallery'|'history';
  const [tab, setTab] = useState<TabId>('overview');
  const [data, setData] = useState<any>({ info:{}, officials:[], messages:[], announcements:[], events:[], gallery:[] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{msg:string;ok:boolean}|null>(null);
  const [expandedMsg, setExpandedMsg] = useState<string|null>(null);
  const [showForm, setShowForm] = useState<string|null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [infoEdit, setInfoEdit] = useState(false);
  const [infoDraft, setInfoDraft] = useState<any>({});

  const showToast = (msg: string, ok = true) => {
    setToast({msg,ok}); setTimeout(()=>setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/public/uzairue-kingdom');
      if (r.ok) { const d = await r.json(); setData(d); setInfoDraft(d.info||{}); }
    } catch { showToast('Failed to load', false); }
    setLoading(false);
  }, []);

  useEffect(()=>{ load(); }, [load]);

  const uploadFile = async (file: File): Promise<string|null> => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch('/api/store/admin/upload-image', { method:'POST', body:fd });
      const d = await r.json();
      setUploading(false);
      return d.success ? d.url : null;
    } catch { setUploading(false); return null; }
  };

  const apiPost = async (path: string, body: any) => {
    const r = await fetch(path, { method:'POST', headers:authHdr(), body:JSON.stringify(body) });
    return r.json();
  };
  const apiPut = async (path: string, body: any) => {
    const r = await fetch(path, { method:'PUT', headers:authHdr(), body:JSON.stringify(body) });
    return r.json();
  };
  const apiDel = async (path: string) => {
    const r = await fetch(path, { method:'DELETE', headers:authHdr() });
    return r.json();
  };

  const saveInfo = async () => {
    setSaving(true);
    const d = await apiPut('/api/admin/uzairue-kingdom', infoDraft);
    if (d.success) { showToast('Kingdom info saved'); setInfoEdit(false); load(); }
    else showToast(d.error||'Failed', false);
    setSaving(false);
  };

  const openAdd = (section: string) => {
    setEditItem(null); setShowForm(section);
    if (section==='officials')     setForm({name:'',title:'',role:'',department:'',phone:'',email:'',photoUrl:''});
    if (section==='messages')      setForm({title:'',occasion:'',date:new Date().toISOString().slice(0,10),excerpt:'',fullText:'',isPublic:true});
    if (section==='announcements') setForm({title:'',body:'',date:new Date().toISOString().slice(0,10),priority:'normal',isPublic:true});
    if (section==='events')        setForm({name:'',date:new Date().toISOString().slice(0,10),location:'',description:'',isPublic:true});
  };
  const openEdit = (section: string, item: any) => {
    setEditItem(item); setShowForm(section); setForm({...item});
  };
  const closeForm = () => { setShowForm(null); setEditItem(null); setForm({}); };

  const saveItem = async (section: string) => {
    setSaving(true);
    const base = `/api/admin/uzairue-kingdom/${section}`;
    const d = editItem
      ? await apiPut(`${base}/${editItem.id}`, form)
      : await apiPost(base, form);
    if (d.success) { showToast(editItem?'Updated':'Added'); closeForm(); load(); }
    else showToast(d.error||'Failed', false);
    setSaving(false);
  };

  const delItem = async (section: string, id: string) => {
    const d = await apiDel(`/api/admin/uzairue-kingdom/${section}/${id}`);
    if (d.success) { showToast('Removed'); load(); }
    else showToast(d.error||'Failed', false);
  };

  const uploadGallery = async (file: File) => {
    const url = await uploadFile(file);
    if (!url) { showToast('Upload failed', false); return; }
    const d = await apiPost('/api/admin/uzairue-kingdom/gallery', { url, caption:'', type:'image' });
    if (d.success) { showToast('Image added'); load(); }
    else showToast(d.error||'Failed', false);
  };

  const priorityColor = (p: string) => p==='urgent'?C.red:p==='high'?C.amber:C.gold;

  const TABS: {id:TabId; label:string; icon:string}[] = [
    {id:'overview',      label:'Overview',       icon:'👑'},
    {id:'officials',     label:'Officials',      icon:'🏛️'},
    {id:'messages',      label:'Royal Messages', icon:'📜'},
    {id:'announcements', label:'Announcements',  icon:'📢'},
    {id:'events',        label:'Cultural Events',icon:'🎭'},
    {id:'gallery',       label:'Gallery',        icon:'🖼️'},
    {id:'history',       label:'History',        icon:'📖'},
  ];

  const { info, officials, messages, announcements, events, gallery } = data;

  return (
    <div style={{background:C.bg, minHeight:'100vh', fontFamily:'Plus Jakarta Sans, sans-serif'}}>

      {toast && (
        <div style={{position:'fixed',top:20,right:20,zIndex:9999,
          background:toast.ok?C.green:C.red,color:'#fff',borderRadius:8,
          padding:'10px 18px',fontSize:13,fontWeight:700,boxShadow:'0 4px 20px rgba(0,0,0,0.5)'}}>
          {toast.ok?'✅':'❌'} {toast.msg}
        </div>
      )}

      {/* Hero */}
      <div style={{background:'linear-gradient(135deg,#0a1a0d,#1a3020,#0a1a0d)',
        borderBottom:`2px solid ${C.gold}`,padding:'36px 24px',textAlign:'center'}}>
        {info.bannerUrl && (
          <img src={info.bannerUrl} alt="Banner"
            style={{width:'100%',maxHeight:200,objectFit:'cover',borderRadius:10,
              marginBottom:16,border:`1px solid ${C.gold}`}} />
        )}
        <div style={{fontSize:36,marginBottom:6}}>👑</div>
        <h1 style={{fontFamily:'Cinzel, serif',color:C.gold,fontSize:26,margin:'0 0 6px'}}>
          Uzairue Kingdom
        </h1>
        <p style={{color:C.muted,fontSize:13,margin:'0 0 4px'}}>{info.motto||'Unity · Tradition · Progress'}</p>
        <p style={{color:C.dim,fontSize:12}}>{info.location||'Etsako West LGA, Edo State, Nigeria'}</p>
        {isAdmin && (
          <label style={{...btn(C.gold,'#000'),cursor:'pointer',marginTop:12,display:'inline-flex'}}>
            <Upload size={13}/> {uploading?'Uploading...':'Change Banner'}
            <input type="file" accept="image/*" style={{display:'none'}}
              onChange={async e=>{
                if(!e.target.files?.[0]) return;
                const url = await uploadFile(e.target.files[0]);
                if(url) { await apiPut('/api/admin/uzairue-kingdom',{...infoDraft,bannerUrl:url}); load(); showToast('Banner updated'); }
                else showToast('Upload failed',false);
              }} />
          </label>
        )}
      </div>

      {/* Tab Bar */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,
        display:'flex',overflowX:'auto',padding:'0 12px'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{background:'none',border:'none',cursor:'pointer',padding:'13px 16px',
              fontSize:12,fontWeight:600,whiteSpace:'nowrap',
              color:tab===t.id?C.gold:C.muted,
              borderBottom:tab===t.id?`2px solid ${C.gold}`:'2px solid transparent'}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{padding:'20px 16px',maxWidth:900,margin:'0 auto'}}>
        {loading && <div style={{textAlign:'center',padding:60,color:C.muted}}>Loading Kingdom Portal...</div>}

        {/* OVERVIEW */}
        {!loading && tab==='overview' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:20}}>
              {[
                {label:'Officials',val:officials.length,icon:'🏛️'},
                {label:'Messages',val:messages.length,icon:'📜'},
                {label:'Announcements',val:announcements.length,icon:'📢'},
                {label:'Events',val:events.length,icon:'🎭'},
                {label:'Gallery',val:gallery.length,icon:'🖼️'},
              ].map(s=>(
                <div key={s.label} style={{background:C.card,border:`1px solid ${C.border}`,
                  borderRadius:10,padding:14,textAlign:'center'}}>
                  <div style={{fontSize:22}}>{s.icon}</div>
                  <div style={{fontSize:22,fontWeight:700,color:C.gold}}>{s.val}</div>
                  <div style={{fontSize:11,color:C.muted}}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0,fontSize:16}}>About Uzairue Kingdom</h3>
                {isAdmin && !infoEdit && (
                  <button onClick={()=>{setInfoEdit(true);setInfoDraft({...info});}} style={btn('#1C2E22',C.gold)}>
                    <Edit2 size={13}/> Edit
                  </button>
                )}
              </div>
              {infoEdit ? (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <textarea rows={4} value={infoDraft.about||''} style={inp} placeholder="Kingdom description..."
                    onChange={e=>setInfoDraft((p:any)=>({...p,about:e.target.value}))} />
                  <input value={infoDraft.motto||''} style={inp} placeholder="Kingdom motto"
                    onChange={e=>setInfoDraft((p:any)=>({...p,motto:e.target.value}))} />
                  <input value={infoDraft.location||''} style={inp} placeholder="Location"
                    onChange={e=>setInfoDraft((p:any)=>({...p,location:e.target.value}))} />
                  <input value={infoDraft.foundedYear||''} style={inp} placeholder="Founded year"
                    onChange={e=>setInfoDraft((p:any)=>({...p,foundedYear:e.target.value}))} />
                  <input value={infoDraft.currentRuler||''} style={inp} placeholder="Current ruler name"
                    onChange={e=>setInfoDraft((p:any)=>({...p,currentRuler:e.target.value}))} />
                  <input value={infoDraft.population||''} style={inp} placeholder="Number of communities"
                    onChange={e=>setInfoDraft((p:any)=>({...p,population:e.target.value}))} />
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={saveInfo} disabled={saving} style={btn(C.gold,'#000')}>
                      <Save size={13}/> {saving?'Saving...':'Save Info'}
                    </button>
                    <button onClick={()=>setInfoEdit(false)} style={btn('#1C2E22',C.text)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{color:C.text,fontSize:14,lineHeight:1.8,margin:'0 0 10px'}}>
                    {info.about||'Kingdom information will appear here once added by the administrator.'}
                  </p>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    {info.currentRuler && <div style={{fontSize:12,color:C.muted}}>👑 Ruler: <span style={{color:C.text}}>{info.currentRuler}</span></div>}
                    {info.foundedYear  && <div style={{fontSize:12,color:C.muted}}>📅 Founded: <span style={{color:C.text}}>{info.foundedYear}</span></div>}
                    {info.population   && <div style={{fontSize:12,color:C.muted}}>👥 Communities: <span style={{color:C.text}}>{info.population}</span></div>}
                    {info.location     && <div style={{fontSize:12,color:C.muted}}>📍 <span style={{color:C.text}}>{info.location}</span></div>}
                  </div>
                </div>
              )}
            </div>
            {announcements.slice(0,3).map((a: any)=>(
              <div key={a.id} style={{background:C.card,
                border:`1px solid ${priorityColor(a.priority)}40`,
                borderLeft:`3px solid ${priorityColor(a.priority)}`,
                borderRadius:8,padding:'10px 14px',marginBottom:8}}>
                <div style={{fontWeight:600,color:C.text,fontSize:13}}>{a.title}</div>
                <div style={{color:C.muted,fontSize:11,marginTop:3}}>{(a.body||'').slice(0,100)}{(a.body||'').length>100?'...':''}</div>
              </div>
            ))}
          </div>
        )}

        {/* OFFICIALS */}
        {!loading && tab==='officials' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0}}>Palace Officials</h3>
              {isAdmin && <button onClick={()=>openAdd('officials')} style={btn(C.gold,'#000')}><Plus size={13}/> Add</button>}
            </div>
            {isAdmin && showForm==='officials' && (
              <div style={{background:C.card,border:`1px solid ${C.gold}40`,borderRadius:12,padding:18,marginBottom:16}}>
                <h4 style={{color:C.gold,marginTop:0}}>{editItem?'Edit Official':'New Official'}</h4>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <input value={form.name||''} style={inp} placeholder="Full Name *"
                    onChange={e=>setForm((p:any)=>({...p,name:e.target.value}))} />
                  <input value={form.title||''} style={inp} placeholder="Title / Honorific"
                    onChange={e=>setForm((p:any)=>({...p,title:e.target.value}))} />
                  <select value={form.role||''} style={inp} onChange={e=>setForm((p:any)=>({...p,role:e.target.value}))}>
                    <option value="">Select Role</option>
                    {ROLES.map(r=><option key={r}>{r}</option>)}
                  </select>
                  <select value={form.department||''} style={inp} onChange={e=>setForm((p:any)=>({...p,department:e.target.value}))}>
                    <option value="">Select Department</option>
                    {DEPT.map(d=><option key={d}>{d}</option>)}
                  </select>
                  <input value={form.phone||''} style={inp} placeholder="Phone"
                    onChange={e=>setForm((p:any)=>({...p,phone:e.target.value}))} />
                  <input value={form.email||''} style={inp} placeholder="Email"
                    onChange={e=>setForm((p:any)=>({...p,email:e.target.value}))} />
                </div>
                <div style={{marginTop:10,display:'flex',alignItems:'center',gap:10}}>
                  <label style={{...btn('#1C2E22',C.text),cursor:'pointer'}}>
                    <Upload size={12}/> {uploading?'Uploading...':'Photo'}
                    <input type="file" accept="image/*" style={{display:'none'}}
                      onChange={async e=>{
                        if(!e.target.files?.[0]) return;
                        const url = await uploadFile(e.target.files[0]);
                        if(url) { setForm((p:any)=>({...p,photoUrl:url})); showToast('Photo ready'); }
                        else showToast('Upload failed',false);
                      }} />
                  </label>
                  {form.photoUrl && <img src={form.photoUrl} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:`1px solid ${C.gold}`}} />}
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}}>
                  <button onClick={()=>saveItem('officials')} disabled={saving} style={btn(C.gold,'#000')}>
                    <Save size={13}/> {saving?'Saving...':'Save'}
                  </button>
                  <button onClick={closeForm} style={btn('#1C2E22',C.text)}>Cancel</button>
                </div>
              </div>
            )}
            {officials.length===0 && <div style={{color:C.muted,fontSize:13,padding:20}}>No officials added yet.</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:12}}>
              {officials.map((o: any)=>(
                <div key={o.id} style={{background:C.card,border:`1px solid ${C.border}`,
                  borderRadius:10,padding:14,display:'flex',gap:10,alignItems:'flex-start'}}>
                  {o.photoUrl
                    ? <img src={o.photoUrl} alt={o.name} style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',border:`2px solid ${C.gold}`,flexShrink:0}} />
                    : <div style={{width:48,height:48,borderRadius:'50%',background:'#1C2E22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>👤</div>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,color:C.text,fontSize:13}}>{o.name}</div>
                    <div style={{fontSize:11,color:C.gold}}>{o.title} {o.role}</div>
                    <div style={{fontSize:11,color:C.muted}}>{o.department}</div>
                    {o.phone && <div style={{fontSize:11,color:C.muted}}>📞 {o.phone}</div>}
                    {isAdmin && (
                      <div style={{display:'flex',gap:5,marginTop:6}}>
                        <button onClick={()=>openEdit('officials',o)} style={btn('#1C2E22',C.gold)}><Edit2 size={12}/></button>
                        <button onClick={()=>delItem('officials',o.id)} style={btn('#2d1515',C.red)}><Trash2 size={12}/></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ROYAL MESSAGES */}
        {!loading && tab==='messages' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0}}>Royal Messages</h3>
              {isAdmin && <button onClick={()=>openAdd('messages')} style={btn(C.gold,'#000')}><Plus size={13}/> Add</button>}
            </div>
            {isAdmin && showForm==='messages' && (
              <div style={{background:C.card,border:`1px solid ${C.gold}40`,borderRadius:12,padding:18,marginBottom:16}}>
                <h4 style={{color:C.gold,marginTop:0}}>{editItem?'Edit Message':'New Royal Message'}</h4>
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <input value={form.title||''} style={inp} placeholder="Message title *"
                    onChange={e=>setForm((p:any)=>({...p,title:e.target.value}))} />
                  <input value={form.occasion||''} style={inp} placeholder="Occasion / Event"
                    onChange={e=>setForm((p:any)=>({...p,occasion:e.target.value}))} />
                  <input type="date" value={form.date||''} style={inp}
                    onChange={e=>setForm((p:any)=>({...p,date:e.target.value}))} />
                  <textarea rows={2} value={form.excerpt||''} style={inp} placeholder="Short excerpt..."
                    onChange={e=>setForm((p:any)=>({...p,excerpt:e.target.value}))} />
                  <textarea rows={5} value={form.fullText||''} style={inp} placeholder="Full message text..."
                    onChange={e=>setForm((p:any)=>({...p,fullText:e.target.value}))} />
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:C.text,fontSize:13}}>
                    <input type="checkbox" checked={form.isPublic!==false}
                      onChange={e=>setForm((p:any)=>({...p,isPublic:e.target.checked}))} /> Make Public
                  </label>
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}}>
                  <button onClick={()=>saveItem('messages')} disabled={saving} style={btn(C.gold,'#000')}>
                    <Save size={13}/> {saving?'Saving...':'Save'}
                  </button>
                  <button onClick={closeForm} style={btn('#1C2E22',C.text)}>Cancel</button>
                </div>
              </div>
            )}
            {messages.length===0 && <div style={{color:C.muted,fontSize:13,padding:20}}>No messages added yet.</div>}
            {messages.map((m: any)=>(
              <div key={m.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontWeight:700,color:C.text,fontSize:14}}>{m.title}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                      📅 {m.date}{m.occasion?` · ${m.occasion}`:''}
                      {!m.isPublic&&<span style={{color:C.amber,marginLeft:8}}>🔒 Draft</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:5}}>
                    <button onClick={()=>setExpandedMsg(expandedMsg===m.id?null:m.id)} style={btn('#1C2E22',C.muted)}>
                      {expandedMsg===m.id?<ChevronUp size={13}/>:<ChevronDown size={13}/>}
                    </button>
                    {isAdmin && <>
                      <button onClick={()=>openEdit('messages',m)} style={btn('#1C2E22',C.gold)}><Edit2 size={12}/></button>
                      <button onClick={()=>delItem('messages',m.id)} style={btn('#2d1515',C.red)}><Trash2 size={12}/></button>
                    </>}
                  </div>
                </div>
                {m.excerpt && <p style={{color:C.muted,fontSize:13,margin:'8px 0 0',lineHeight:1.6}}>{m.excerpt}</p>}
                {expandedMsg===m.id && m.fullText && (
                  <div style={{marginTop:10,padding:12,background:C.bg,borderRadius:8,color:C.text,fontSize:13,lineHeight:1.8,whiteSpace:'pre-wrap'}}>
                    {m.fullText}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ANNOUNCEMENTS */}
        {!loading && tab==='announcements' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0}}>Kingdom Announcements</h3>
              {isAdmin && <button onClick={()=>openAdd('announcements')} style={btn(C.gold,'#000')}><Plus size={13}/> Add</button>}
            </div>
            {isAdmin && showForm==='announcements' && (
              <div style={{background:C.card,border:`1px solid ${C.gold}40`,borderRadius:12,padding:18,marginBottom:16}}>
                <h4 style={{color:C.gold,marginTop:0}}>{editItem?'Edit Announcement':'New Announcement'}</h4>
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <input value={form.title||''} style={inp} placeholder="Title *"
                    onChange={e=>setForm((p:any)=>({...p,title:e.target.value}))} />
                  <textarea rows={3} value={form.body||''} style={inp} placeholder="Announcement body..."
                    onChange={e=>setForm((p:any)=>({...p,body:e.target.value}))} />
                  <input type="date" value={form.date||''} style={inp}
                    onChange={e=>setForm((p:any)=>({...p,date:e.target.value}))} />
                  <select value={form.priority||'normal'} style={inp}
                    onChange={e=>setForm((p:any)=>({...p,priority:e.target.value}))}>
                    <option value="normal">Normal</option>
                    <option value="high">High Priority</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:C.text,fontSize:13}}>
                    <input type="checkbox" checked={form.isPublic!==false}
                      onChange={e=>setForm((p:any)=>({...p,isPublic:e.target.checked}))} /> Make Public
                  </label>
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}}>
                  <button onClick={()=>saveItem('announcements')} disabled={saving} style={btn(C.gold,'#000')}>
                    <Save size={13}/> {saving?'Saving...':'Save'}
                  </button>
                  <button onClick={closeForm} style={btn('#1C2E22',C.text)}>Cancel</button>
                </div>
              </div>
            )}
            {announcements.length===0 && <div style={{color:C.muted,fontSize:13,padding:20}}>No announcements yet.</div>}
            {announcements.map((a: any)=>(
              <div key={a.id} style={{background:C.card,
                border:`1px solid ${priorityColor(a.priority)}50`,
                borderLeft:`4px solid ${priorityColor(a.priority)}`,
                borderRadius:10,padding:14,marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <span style={{fontWeight:700,color:C.text,fontSize:14}}>{a.title}</span>
                    <span style={{fontSize:10,color:priorityColor(a.priority),textTransform:'uppercase',
                      background:`${priorityColor(a.priority)}20`,padding:'2px 7px',borderRadius:4,marginLeft:8}}>
                      {a.priority}
                    </span>
                    {!a.isPublic&&<span style={{fontSize:10,color:C.amber,marginLeft:6}}>🔒 Draft</span>}
                  </div>
                  {isAdmin && (
                    <div style={{display:'flex',gap:5}}>
                      <button onClick={()=>openEdit('announcements',a)} style={btn('#1C2E22',C.gold)}><Edit2 size={12}/></button>
                      <button onClick={()=>delItem('announcements',a.id)} style={btn('#2d1515',C.red)}><Trash2 size={12}/></button>
                    </div>
                  )}
                </div>
                <p style={{color:C.muted,fontSize:13,margin:'6px 0 0',lineHeight:1.6}}>{a.body}</p>
                <div style={{fontSize:11,color:C.dim,marginTop:4}}>📅 {a.date}</div>
              </div>
            ))}
          </div>
        )}

        {/* CULTURAL EVENTS */}
        {!loading && tab==='events' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0}}>Cultural Events</h3>
              {isAdmin && <button onClick={()=>openAdd('events')} style={btn(C.gold,'#000')}><Plus size={13}/> Add</button>}
            </div>
            {isAdmin && showForm==='events' && (
              <div style={{background:C.card,border:`1px solid ${C.gold}40`,borderRadius:12,padding:18,marginBottom:16}}>
                <h4 style={{color:C.gold,marginTop:0}}>{editItem?'Edit Event':'New Cultural Event'}</h4>
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <input value={form.name||''} style={inp} placeholder="Event name *"
                    onChange={e=>setForm((p:any)=>({...p,name:e.target.value}))} />
                  <input type="date" value={form.date||''} style={inp}
                    onChange={e=>setForm((p:any)=>({...p,date:e.target.value}))} />
                  <input value={form.location||''} style={inp} placeholder="Location"
                    onChange={e=>setForm((p:any)=>({...p,location:e.target.value}))} />
                  <textarea rows={3} value={form.description||''} style={inp} placeholder="Description..."
                    onChange={e=>setForm((p:any)=>({...p,description:e.target.value}))} />
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:C.text,fontSize:13}}>
                    <input type="checkbox" checked={form.isPublic!==false}
                      onChange={e=>setForm((p:any)=>({...p,isPublic:e.target.checked}))} /> Make Public
                  </label>
                </div>
                <div style={{display:'flex',gap:8,marginTop:12}}>
                  <button onClick={()=>saveItem('events')} disabled={saving} style={btn(C.gold,'#000')}>
                    <Save size={13}/> {saving?'Saving...':'Save'}
                  </button>
                  <button onClick={closeForm} style={btn('#1C2E22',C.text)}>Cancel</button>
                </div>
              </div>
            )}
            {events.length===0 && <div style={{color:C.muted,fontSize:13,padding:20}}>No cultural events added yet.</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:12}}>
              {events.map((e: any)=>(
                <div key={e.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}>
                  <div style={{fontSize:20,marginBottom:6}}>🎭</div>
                  <div style={{fontWeight:700,color:C.text,fontSize:14}}>{e.name}</div>
                  <div style={{fontSize:12,color:C.gold,marginTop:4}}>📅 {e.date}</div>
                  {e.location && <div style={{fontSize:12,color:C.muted}}>📍 {e.location}</div>}
                  {e.description && <p style={{fontSize:12,color:C.muted,marginTop:6,lineHeight:1.5}}>{e.description}</p>}
                  {!e.isPublic && <div style={{fontSize:11,color:C.amber}}>🔒 Draft</div>}
                  {isAdmin && (
                    <div style={{display:'flex',gap:5,marginTop:8}}>
                      <button onClick={()=>openEdit('events',e)} style={btn('#1C2E22',C.gold)}><Edit2 size={12}/></button>
                      <button onClick={()=>delItem('events',e.id)} style={btn('#2d1515',C.red)}><Trash2 size={12}/></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GALLERY */}
        {!loading && tab==='gallery' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',margin:0}}>Kingdom Gallery</h3>
              {isAdmin && (
                <label style={{...btn(C.gold,'#000'),cursor:'pointer'}}>
                  <Upload size={13}/> {uploading?'Uploading...':'Upload Image'}
                  <input type="file" accept="image/*,video/*" style={{display:'none'}}
                    onChange={e=>{ if(e.target.files?.[0]) uploadGallery(e.target.files[0]); }} />
                </label>
              )}
            </div>
            {gallery.length===0 && <div style={{color:C.muted,fontSize:13,padding:20}}>No gallery items yet.</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:10}}>
              {gallery.map((g: any)=>(
                <div key={g.id} style={{position:'relative',borderRadius:8,overflow:'hidden',
                  border:`1px solid ${C.border}`,background:C.card}}>
                  <img src={g.url} alt={g.caption||''}
                    style={{width:'100%',height:130,objectFit:'cover',display:'block'}} />
                  {g.caption && <div style={{padding:'5px 8px',fontSize:11,color:C.muted}}>{g.caption}</div>}
                  {isAdmin && (
                    <button onClick={()=>delItem('gallery',g.id)}
                      style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.7)',
                        border:'none',borderRadius:4,color:C.red,cursor:'pointer',padding:'3px 6px'}}>
                      <Trash2 size={11}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HISTORY */}
        {!loading && tab==='history' && (
          <div>
            <h3 style={{color:C.gold,fontFamily:'Cinzel, serif',marginTop:0}}>Kingdom History</h3>
            {isAdmin ? (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                <textarea rows={14} value={infoDraft.history||''} style={inp}
                  placeholder="Write the full Kingdom history here..."
                  onChange={e=>setInfoDraft((p:any)=>({...p,history:e.target.value}))} />
                <button onClick={saveInfo} disabled={saving} style={btn(C.gold,'#000')}>
                  <Save size={13}/> {saving?'Saving...':'Save History'}
                </button>
              </div>
            ) : (
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,
                padding:20,color:C.text,fontSize:14,lineHeight:1.9,whiteSpace:'pre-wrap'}}>
                {info.history||'Kingdom history coming soon.'}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
